import { useState } from "react";
import { useAppStore, selectCurrentUser, selectCurrentRole } from "@state/store";
import {
  ROLES,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  can,
  type Role,
} from "@data/index";

/**
 * Members & Access (Identity mandate). Lives inside Settings. The owner can
 * invite members, reassign roles, and remove accounts; everyone else sees a
 * read-only roster. All writes go through the store, which re-checks the
 * owner-only permission — this UI just reflects and guards it.
 */
export function MembersAccess() {
  const users = useAppStore((s) => s.users);
  const currentUser = useAppStore(selectCurrentUser);
  const canManage = can(useAppStore(selectCurrentRole), "members.manage");
  const inviteMember = useAppStore((s) => s.inviteMember);
  const updateMemberRole = useAppStore((s) => s.updateMemberRole);
  const removeMember = useAppStore((s) => s.removeMember);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const canInvite =
    name.trim().length > 0 && emailValid && password.length >= 8;

  const sorted = [...users].sort((a, b) => a.name.localeCompare(b.name));

  async function invite() {
    setError(null);
    setNotice(null);
    try {
      await inviteMember({ name, email, role, password });
      setNotice(`Invited ${name.trim()} as ${ROLE_LABELS[role]}.`);
      setName("");
      setEmail("");
      setPassword("");
      setRole("member");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t invite that member.");
    }
  }

  async function changeRole(id: string, next: Role) {
    setError(null);
    setNotice(null);
    try {
      await updateMemberRole(id, next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t change that role.");
    }
  }

  async function remove(id: string, memberName: string) {
    setError(null);
    setNotice(null);
    try {
      await removeMember(id);
      setNotice(`Removed ${memberName}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t remove that member.");
    }
  }

  return (
    <div className="card" style={{ marginTop: "var(--s4)" }}>
      <div className="card-head">
        <h2>Members &amp; access</h2>
        <span className="top-spacer" />
        <span className="badge neutral">{users.length} member{users.length === 1 ? "" : "s"}</span>
      </div>
      <div className="card-body">
        <p className="hint" style={{ marginTop: 0 }}>
          Roles scope every action to this organization. {ROLE_DESCRIPTIONS.owner}{" "}
          Only the owner can manage members.
        </p>

        {canManage ? (
          <div className="row mt4" style={{ gap: "var(--s4)", alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label htmlFor="mName">Name</label>
              <input
                id="mName"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Omar Haddad"
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 180 }}>
              <label htmlFor="mEmail">Email</label>
              <input
                id="mEmail"
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="member@company.com"
              />
            </div>
            <div className="field" style={{ width: 150 }}>
              <label htmlFor="mRole">Role</label>
              <select
                id="mRole"
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 160 }}>
              <label htmlFor="mPassword">Temp password</label>
              <input
                id="mPassword"
                className="input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button type="button" className="btn primary" onClick={invite} disabled={!canInvite}>
              Invite member
            </button>
          </div>
        ) : (
          <div className="role-note mt4" role="note">
            You’re signed in as {currentUser ? ROLE_LABELS[currentUser.role] : "a member"} —
            only the owner can invite or change members.
          </div>
        )}

        {error ? (
          <div className="error-banner mt3" role="alert">
            {error}
          </div>
        ) : null}
        {notice ? (
          <div className="badge good mt3">
            <span className="dot" /> {notice}
          </div>
        ) : null}

        <div className="table-wrap mt4">
          <table className="data">
            <thead>
              <tr>
                <th>Member</th>
                <th>Email</th>
                <th>Role</th>
                {canManage ? (
                  <th>
                    <span className="visually-hidden">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {sorted.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id}>
                    <td>
                      {u.name}
                      {isSelf ? <span className="muted"> · you</span> : null}
                    </td>
                    <td className="muted">{u.email}</td>
                    <td>
                      {canManage ? (
                        <select
                          className="input"
                          aria-label={`Role for ${u.name}`}
                          value={u.role}
                          style={{ maxWidth: 140 }}
                          onChange={(e) => void changeRole(u.id, e.target.value as Role)}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="badge neutral">{ROLE_LABELS[u.role]}</span>
                      )}
                    </td>
                    {canManage ? (
                      <td className="num">
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={`Remove ${u.name}`}
                          disabled={isSelf}
                          onClick={() => void remove(u.id, u.name)}
                        >
                          ✕
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
